/**
 * Icon Mappings for Person Attributes
 * ⚠️ FRONTEND ONLY - Backend should not import this file
 *
 * Maps attribute values to icon paths
 */
import { Gender, AgeGroup, FacialHair, Glasses, Height, Build, SkinTone, HairColor, HairStyle, EyeColor, Dress, InterestLevel } from './attributes.js';

export const GenderIcons = Object.freeze({
  [Gender.MALE]: 'images/icons8-male-50.png',
  [Gender.FEMALE]: 'images/icons8-female-50.png',
  [Gender.OTHER]: 'images/icons8-agender-symbol-50.png'
});

export const AgeGroupIcons = Object.freeze({
  [AgeGroup.YOUNG]: 'images/L30.png',
  [AgeGroup.THIRTIES]: 'images/G30.png',
  [AgeGroup.FORTIES]: 'images/G40.png',
  [AgeGroup.FIFTIES]: 'images/G50.png',
  [AgeGroup.SIXTIES]: 'images/G60.png',
  [AgeGroup.SEVENTYPLUS]: 'images/G70.png'
});

export const FacialHairIcons = Object.freeze({
  [FacialHair.NONE]: 'images/icons8-face-50.png',
  [FacialHair.MOUSTACHE]: 'images/icons8-mustache-50.png',
  [FacialHair.BEARD]: 'images/icons8-short-beard-50.png',
  [FacialHair.GOATEE]: 'images/icons8-goatee-50.png'
});

export const GlassesIcons = Object.freeze({
  [Glasses.YES]: 'images/icons8-glasses-50.png',
  [Glasses.NO]: 'images/icons8-eyes-50.png'
});

export const HeightIcons = Object.freeze({
  [Height.SHORT]: 'images/icons8-body-type-short-50.png',
  [Height.AVG]: 'images/icons8-body-type-average-50.png',
  [Height.TALL]: 'images/icons8-body-type-tall-50.png',
  [Height.GIANT]: 'images/icons8-body-type-tall-50.png'
});

export const BuildIcons = Object.freeze({
  [Build.SKINNY]: 'images/icons8-bodyanno-51.png',
  [Build.SLIM]: 'images/icons8-bodythin-51.png',
  [Build.AVG]: 'images/icons8-bodyavg-51.png',
  [Build.TUBBY]: 'images/icons8-bodytubby-51.png',
  [Build.FAT]: 'images/icons8-bodyfat-51.png',
  [Build.HUGE]: 'images/icons8-bodyhuge-51.png'
});

export const SkinToneIcons = Object.freeze({
  [SkinTone.WHITE]: 'images/FZP1.png',
  [SkinTone.ASIAN]: 'images/FZP4.png',
  [SkinTone.BLACK]: 'images/FZP6.png'
});

export const HairColorIcons = Object.freeze({
  [HairColor.BLONDE]: 'images/hair-blonde.png',
  [HairColor.BROWN]: 'images/hair-brown.png',
  [HairColor.BLACK]: 'images/hair-black.png',
  [HairColor.GREY]: 'images/hair-gray.png',
  [HairColor.RED]: 'images/hair-red.png'
});

export const HairStyleIcons = Object.freeze({
  [HairStyle.BALD]: 'images/hair-bald.png',
  [HairStyle.SHORT]: 'images/hair-short.png',
  [HairStyle.BOB]: 'images/hair-bob.png',
  [HairStyle.SHOULDER]: 'images/hair-shoulder.png',
  [HairStyle.LONG]: 'images/hair-long.png'
});

export const EyeColorIcons = Object.freeze({
  [EyeColor.GREY]: 'images/eye-grey.png',
  [EyeColor.BLUE]: 'images/eye-blue.png',
  [EyeColor.GREEN]: 'images/eye-green.png',
  [EyeColor.BROWN]: 'images/eye-brown.png',
  [EyeColor.HAZEL]: 'images/eye-hazel.png'
});

export const DressIcons = Object.freeze({
  [Dress.FORMAL]: 'images/icons8-formal-50.png',
  [Dress.BUSINESS]: 'images/icons8-business-50.png',
  [Dress.CASUAL]: 'images/icons8-casual-50.png'
});

/**
 * Interest Level Colors
 * Used in aggregator UI - colors instead of icons
 */
export const InterestLevelColors = Object.freeze({
  [InterestLevel.CRITICAL]: '#d32f2f', // red
  [InterestLevel.HIGH]: '#f57c00',     // orange
  [InterestLevel.MEDIUM]: '#fbc02d',   // yellow
  [InterestLevel.LOW]: '#9e9e9e',      // grey
  [InterestLevel.NONE]: '#9e9e9e'      // grey
});

/**
 * Master icon map - consolidates all attribute icons
 * Replaces the legacy imagemap.json
 */
export const AttributeIconMap = Object.freeze({
  Gender: GenderIcons,
  AgeGroup: AgeGroupIcons,
  FacialHair: FacialHairIcons,
  Glasses: GlassesIcons,
  Height: HeightIcons,
  Build: BuildIcons,
  SkinTone: SkinToneIcons,
  HairColor: HairColorIcons,
  HairStyle: HairStyleIcons,
  EyeColor: EyeColorIcons,
  Dress: DressIcons
});

/**
 * Get icon path for a given attribute type and value
 * @param {string} enumType - Attribute type (e.g., 'Gender', 'AgeGroup')
 * @param {string} entryKey - Attribute value (e.g., 'MALE', 'THIRTIES')
 * @returns {string|undefined} Icon path or undefined if not found
 */
export function getAttributeIcon(enumType, entryKey) {
  const iconMap = AttributeIconMap[enumType];
  if (!iconMap) {
    console.warn(`[AttributeIcons] No icon map found for enumType="${enumType}"`);
    return undefined;
  }

  const iconPath = iconMap[entryKey];
  if (!iconPath) {
    console.warn(`[AttributeIcons] No icon found for "${enumType}.${entryKey}"`);
    console.log(`[AttributeIcons] Available keys:`, Object.keys(iconMap));
    return undefined;
  }

  return iconPath;
}
